import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Rating,
  Switch,
  FormControlLabel,
  Paper
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { api } from '@services/authService';

interface PrecheckData {
  chief_complaint: string;
  onset?: 'acute' | 'subacute' | 'chronic';
  duration_days?: number;
  severity: 'mild' | 'moderate' | 'severe' | 'emergency';
  pain_score?: number;
  appetite: 'normal' | 'decreased' | 'none';
  water_intake: 'decreased' | 'normal' | 'increased';
  vomiting: { present: boolean; freq?: string };
  diarrhea: { present: boolean; freq?: string };
  urination?: string;
  behavior?: string;
  current_meds?: string[];
  allergies?: string[];
  diet?: string;
  repro_status?: string;
  free_text?: string;
}

const steps = ['Complaint Principal', 'Simptome', 'Comportament', 'Medicație', 'Confirmare'];

const PreCheckInWizard: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [precheckInfo, setPrecheckInfo] = useState<any>(null);
  const [expired, setExpired] = useState(false);

  const [formData, setFormData] = useState<PrecheckData>({
    chief_complaint: '',
    severity: 'mild',
    appetite: 'normal',
    water_intake: 'normal',
    vomiting: { present: false },
    diarrhea: { present: false },
    current_meds: [],
    allergies: []
  });

  // Load precheck info
  useEffect(() => {
    const loadPrecheck = async () => {
      try {
        const response = await api.get(`/precheck/${token}`);
        if (response.data.success) {
          setPrecheckInfo(response.data.data);
          if (response.data.data.status === 'completed') {
            setError('Pre-check-in-ul a fost deja completat');
          } else if (response.data.data.status === 'expired') {
            setExpired(true);
          }
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Eroare la încărcarea pre-check-in');
        if (err.response?.status === 404) {
          setExpired(true);
        }
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      loadPrecheck();
    }
  }, [token]);

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const response = await api.post(`/precheck/${token}`, formData);
      if (response.data.success) {
        navigate('/appointments', { state: { message: 'Pre-check-in completat cu succes!' } });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Eroare la trimiterea pre-check-in');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (expired) {
    return (
      <Box p={3}>
        <Alert severity="error" icon={<ErrorIcon />}>
          Link-ul de pre-check-in a expirat. Vă rugăm să contactați clinica.
        </Alert>
      </Box>
    );
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Chief Complaint
        return (
          <Stack spacing={3}>
            <Typography variant="h6">Care este problema principală?</Typography>
            <TextField
              label="Complaint Principal"
              multiline
              rows={4}
              fullWidth
              required
              value={formData.chief_complaint}
              onChange={(e) => setFormData({ ...formData, chief_complaint: e.target.value })}
              placeholder="Descrieți problema principală a animalului..."
            />
            <FormControl fullWidth>
              <InputLabel>Severitate</InputLabel>
              <Select
                value={formData.severity}
                onChange={(e) => setFormData({ ...formData, severity: e.target.value as any })}
              >
                <MenuItem value="mild">Ușoară</MenuItem>
                <MenuItem value="moderate">Moderată</MenuItem>
                <MenuItem value="severe">Severă</MenuItem>
                <MenuItem value="emergency">Urgență</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Onset</InputLabel>
              <Select
                value={formData.onset || ''}
                onChange={(e) => setFormData({ ...formData, onset: e.target.value as any })}
              >
                <MenuItem value="acute">Acut (&lt; 24h)</MenuItem>
                <MenuItem value="subacute">Subacut (1-7 zile)</MenuItem>
                <MenuItem value="chronic">Cronic (&gt; 7 zile)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Durată (zile)"
              type="number"
              fullWidth
              value={formData.duration_days || ''}
              onChange={(e) => setFormData({ ...formData, duration_days: parseInt(e.target.value) || undefined })}
            />
          </Stack>
        );

      case 1: // Symptoms
        return (
          <Stack spacing={3}>
            <Typography variant="h6">Simptome</Typography>
            
            <Box>
              <Typography variant="subtitle2" gutterBottom>Scor Durere (0-10)</Typography>
              <Rating
                max={10}
                value={formData.pain_score || 0}
                onChange={(_, value) => setFormData({ ...formData, pain_score: value || 0 })}
              />
            </Box>

            <FormControl fullWidth>
              <InputLabel>Poftă de mâncare</InputLabel>
              <Select
                value={formData.appetite}
                onChange={(e) => setFormData({ ...formData, appetite: e.target.value as any })}
              >
                <MenuItem value="normal">Normală</MenuItem>
                <MenuItem value="decreased">Scăzută</MenuItem>
                <MenuItem value="none">Absentă</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Consum apă</InputLabel>
              <Select
                value={formData.water_intake}
                onChange={(e) => setFormData({ ...formData, water_intake: e.target.value as any })}
              >
                <MenuItem value="decreased">Scăzut</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="increased">Crescut</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.vomiting.present}
                  onChange={(e) => setFormData({
                    ...formData,
                    vomiting: { ...formData.vomiting, present: e.target.checked }
                  })}
                />
              }
              label="Vomă"
            />
            {formData.vomiting.present && (
              <TextField
                label="Frecvență vomă"
                value={formData.vomiting.freq || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  vomiting: { ...formData.vomiting, freq: e.target.value }
                })}
                placeholder="ex: 2x/zi"
              />
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={formData.diarrhea.present}
                  onChange={(e) => setFormData({
                    ...formData,
                    diarrhea: { ...formData.diarrhea, present: e.target.checked }
                  })}
                />
              }
              label="Diarree"
            />
            {formData.diarrhea.present && (
              <TextField
                label="Frecvență diarree"
                value={formData.diarrhea.freq || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  diarrhea: { ...formData.diarrhea, freq: e.target.value }
                })}
                placeholder="ex: 3x/zi"
              />
            )}
          </Stack>
        );

      case 2: // Behavior
        return (
          <Stack spacing={3}>
            <Typography variant="h6">Comportament și Altele</Typography>
            <TextField
              label="Comportament"
              multiline
              rows={3}
              fullWidth
              value={formData.behavior || ''}
              onChange={(e) => setFormData({ ...formData, behavior: e.target.value })}
              placeholder="Descrieți comportamentul animalului..."
            />
            <TextField
              label="Urinare"
              multiline
              rows={2}
              fullWidth
              value={formData.urination || ''}
              onChange={(e) => setFormData({ ...formData, urination: e.target.value })}
              placeholder="Observații despre urinare..."
            />
            <TextField
              label="Dietă"
              fullWidth
              value={formData.diet || ''}
              onChange={(e) => setFormData({ ...formData, diet: e.target.value })}
              placeholder="Tipul de hrană consumată..."
            />
            <TextField
              label="Status Reproductiv"
              fullWidth
              value={formData.repro_status || ''}
              onChange={(e) => setFormData({ ...formData, repro_status: e.target.value })}
              placeholder="ex: neutered, intact, spayed"
            />
          </Stack>
        );

      case 3: // Medication
        return (
          <Stack spacing={3}>
            <Typography variant="h6">Medicație și Alergii</Typography>
            <TextField
              label="Medicație actuală"
              multiline
              rows={3}
              fullWidth
              value={(formData.current_meds || []).join(', ')}
              onChange={(e) => setFormData({
                ...formData,
                current_meds: e.target.value.split(',').map(s => s.trim()).filter(s => s)
              })}
              placeholder="Lista medicamentelor actuale (separate prin virgulă)..."
            />
            <TextField
              label="Alergii cunoscute"
              multiline
              rows={2}
              fullWidth
              value={(formData.allergies || []).join(', ')}
              onChange={(e) => setFormData({
                ...formData,
                allergies: e.target.value.split(',').map(s => s.trim()).filter(s => s)
              })}
              placeholder="Lista alergiilor (separate prin virgulă)..."
            />
            <TextField
              label="Alte informații"
              multiline
              rows={4}
              fullWidth
              value={formData.free_text || ''}
              onChange={(e) => setFormData({ ...formData, free_text: e.target.value })}
              placeholder="Orice altă informație relevantă..."
            />
          </Stack>
        );

      case 4: // Confirmation
        return (
          <Stack spacing={3}>
            <Typography variant="h6">Confirmare</Typography>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Rezumat:</Typography>
              <Typography><strong>Complaint:</strong> {formData.chief_complaint}</Typography>
              <Typography><strong>Severitate:</strong> {formData.severity}</Typography>
              {formData.pain_score !== undefined && (
                <Typography><strong>Scor durere:</strong> {formData.pain_score}/10</Typography>
              )}
            </Paper>
            <Alert severity="info">
              După trimitere, veterinarul va vedea aceste informații înainte de consultație.
            </Alert>
          </Stack>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Card>
        <CardContent>
          <Typography variant="h4" gutterBottom align="center">
            Pre-Check-In
          </Typography>
          {precheckInfo && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Programare pentru: {precheckInfo.appointment_date}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {renderStepContent()}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Înapoi
            </Button>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting || !formData.chief_complaint}
                startIcon={submitting ? <CircularProgress size={20} /> : <CheckCircleIcon />}
              >
                {submitting ? 'Se trimite...' : 'Trimite Pre-Check-In'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={activeStep === 0 && !formData.chief_complaint}
              >
                Următorul
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PreCheckInWizard;

